import type { PointerEvent } from "react";
import type { CanvasEditor, CanvasEditorSnapshot } from "../controller/types";

const WIDTH = 184;
const HEIGHT = 116;
const PADDING = 8;

export function CanvasMinimap({
  editor,
  snapshot,
}: {
  readonly editor: CanvasEditor;
  readonly snapshot: CanvasEditorSnapshot;
}) {
  if (snapshot.canvas.nodes.length === 0) return null;
  const minX = Math.min(...snapshot.canvas.nodes.map((node) => node.geometry.x));
  const minY = Math.min(...snapshot.canvas.nodes.map((node) => node.geometry.y));
  const maxX = Math.max(...snapshot.canvas.nodes.map((node) => node.geometry.x + node.geometry.width));
  const maxY = Math.max(...snapshot.canvas.nodes.map((node) => node.geometry.y + node.geometry.height));
  const viewMinX = -snapshot.view.panX / snapshot.view.zoom;
  const viewMinY = -snapshot.view.panY / snapshot.view.zoom;
  const viewMaxX = viewMinX + snapshot.view.viewportWidth / snapshot.view.zoom;
  const viewMaxY = viewMinY + snapshot.view.viewportHeight / snapshot.view.zoom;
  const worldMinX = Math.min(minX, viewMinX);
  const worldMinY = Math.min(minY, viewMinY);
  const worldMaxX = Math.max(maxX, viewMaxX);
  const worldMaxY = Math.max(maxY, viewMaxY);
  const scale = Math.min(
    (WIDTH - PADDING * 2) / Math.max(1, worldMaxX - worldMinX),
    (HEIGHT - PADDING * 2) / Math.max(1, worldMaxY - worldMinY),
  );
  const mapX = (value: number) => PADDING + (value - worldMinX) * scale;
  const mapY = (value: number) => PADDING + (value - worldMinY) * scale;
  const navigate = (event: PointerEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const targetX = worldMinX + (event.clientX - bounds.left - PADDING) / scale;
    const targetY = worldMinY + (event.clientY - bounds.top - PADDING) / scale;
    const panX = snapshot.view.viewportWidth / 2 - targetX * snapshot.view.zoom;
    const panY = snapshot.view.viewportHeight / 2 - targetY * snapshot.view.zoom;
    editor.panBy(panX - snapshot.view.panX, panY - snapshot.view.panY);
  };
  return (
    <aside
      aria-label="画板小地图"
      style={{ position: "absolute", zIndex: 18, left: 12, bottom: 12, width: WIDTH, padding: 4, border: "1px solid #cbd5e1", borderRadius: 10, background: "rgba(255,255,255,.94)", boxShadow: "0 8px 24px rgba(15,23,42,.12)" }}
    >
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="navigation"
        aria-label="点击小地图快速定位"
        onPointerDown={navigate}
        style={{ display: "block", cursor: "crosshair" }}
      >
        {snapshot.canvas.nodes.map((node) => (
          <rect
            key={node.id}
            x={mapX(node.geometry.x)}
            y={mapY(node.geometry.y)}
            width={Math.max(2, node.geometry.width * scale)}
            height={Math.max(2, node.geometry.height * scale)}
            rx={1.5}
            fill={snapshot.selection.nodeIds.has(node.id) ? "#4f46e5" : node.props.locked === true ? "#94a3b8" : "#cbd5e1"}
          />
        ))}
        <rect
          x={mapX(viewMinX)}
          y={mapY(viewMinY)}
          width={Math.max(4, (viewMaxX - viewMinX) * scale)}
          height={Math.max(4, (viewMaxY - viewMinY) * scale)}
          fill="rgba(79,70,229,.08)"
          stroke="#4f46e5"
          strokeWidth={1.5}
        />
      </svg>
    </aside>
  );
}
